/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import {
  CHARGE_TABLE_DATA,
  CHARGING_ITEM,
  DEFAULT_ZERO_QUANTITY,
  OFFER_TYPE,
  OffersType,
  PAYMENT_METHOD,
  PaymentMethod,
  UNIT_PRICES,
} from 'pages/ProductsPages/constant'
import {useTranslation} from 'react-i18next'
import {PricesInterface, ProductAvailableType, SaleTarget} from 'api/productApi'
import useProductManagement from 'features/productManagement/useProductManagement'
import i18n from 'i18n'
import useStyles from 'components/Dialog/Product/style'
import RouteConstant from 'constants/RouteConstant'
import {navigate} from 'AppHistory'
import {formatPriceNumber} from 'helpers/uiHelper'
import useOrganization from 'features/org/useOrganization'
import useFailureModal from 'features/modal/useFailureModal'
import {PACKAGE_AVAILABLE_IMAGE_ID} from 'pages/ProductOrderPages/constants'
import {useAppDispatch} from 'hooks'
import {
  openReportImageDialog,
  setReportDialogId,
} from 'features/modal/modalSlice'

type ChargeProductDialogProps = {
  open: boolean
  onClose: () => void
  typeCharge: PaymentMethod
  productName: string
  paymentMethod: string
  productSale: ProductAvailableType['sale']
  offerType: OffersType
  duration: number
  description: string
  goods: (ProductAvailableType['offer']['offerItems'][number] & {
    usageAmount: number
    order: number
  })[]
  goodsId: string
  licenseId: string
  saleTarget: SaleTarget
}

const ChargeProductDialog = (props: ChargeProductDialogProps) => {
  const {
    open,
    onClose,
    productName,
    typeCharge,
    paymentMethod,
    duration,
    description,
    goods,
    offerType,
    goodsId,
    licenseId,
    saleTarget,
    productSale,
  } = props
  const classes = useStyles()
  const {t} = useTranslation()
  const seenCls = new Set<string>()
  const {listPackage, onSetIsAgreeToPurchase} = useProductManagement()
  const ref = useRef<HTMLDivElement>(null)
  const [chargeAmount, setChargeAmount] = React.useState<
    {quantity: number; pkgItemId: string; offerAmount: number}[]
  >(
    goods.map((item) => ({
      quantity: DEFAULT_ZERO_QUANTITY,
      pkgItemId: item.pkgItemId,
      offerAmount: item.offerAmount,
    })),
  )
  const [manualChargeQuantity, setManualChargeQuantity] = useState<number>(0)
  const [isReadTerms, setIsReadTerms] = useState<boolean>(false)
  const {organization} = useOrganization()
  const {onOpen: onOpenFailureModal} = useFailureModal()

  const dispatch = useAppDispatch()

  const getCLS = (id: string) => {
    if (!listPackage) return ''
    const item = listPackage.filter((e) => e.itemId === id)
    if (item.length) {
      return item[0].cls
    }
    return ''
  }

  const getPackageName = (id: string) => {
    if (!listPackage) return ''
    const item = listPackage.filter((e) => e.itemId === id)
    return (
      item[0]?.displayInfos?.find(
        (info) => info.langCode === (i18n.language === 'ko' ? 'ko' : 'en'),
      )?.name || ''
    )
  }

  const onChangeChargeAmount = useCallback(
    (pkgItemId: string, quantity: number) => {
      setChargeAmount((prev) =>
        prev.map((item) =>
          item.pkgItemId === pkgItemId ? {...item, quantity} : item,
        ),
      )
    },
    [],
  )

  const data = useMemo(() => {
    const value = goods
      .map((item) => {
        return {
          ...item,
          cls: getCLS(item.pkgItemId),
          pkgName: getPackageName(item.pkgItemId),
        }
      })
      .toSorted((a, b) => a.cls.localeCompare(b.cls) || a.order - b.order)

    return value
  }, [goods])

  const rowSpanMap = data.reduce<Record<string, number>>((acc, curr) => {
    acc[curr.cls] = (acc[curr.cls] || 0) + 1
    return acc
  }, {})

  const contentGuide = useMemo(() => {
    if (typeCharge === PAYMENT_METHOD.MANUAL) {
      return t('IUserGuideDescSettlement')
    }
    if (typeCharge === PAYMENT_METHOD.PREPAYMENT) {
      if (offerType === OFFER_TYPE.ONCE) {
        return t('IUserGuideDescBulk')
      }
      if (offerType === OFFER_TYPE.MONTHLY) {
        return t('IUserGuideDescMonthly')
      }
    }
    return ''
  }, [typeCharge, i18n.language])

  const targetCurrency = useMemo(() => {
    return organization?.country.code === 'KR'
      ? UNIT_PRICES.KRW
      : UNIT_PRICES.USD
  }, [saleTarget])

  const getPriceByLanguage = useCallback(
    (prices) => {
      const priceObject = prices.find(
        (item: PricesInterface) => item.currency === targetCurrency,
      )
      return priceObject ? priceObject.price : 0
    },
    [t, targetCurrency],
  )

  const totalChargeAmount = useMemo(() => {
    const chargeAmountReduce = goods.map((item) => {
      const price = getPriceByLanguage(item.unitPrices)
      const unitPrice = chargeAmount.find(
        (charge) => charge.pkgItemId === item.pkgItemId,
      )
      return price * (unitPrice?.quantity || 0)
    })
    return chargeAmountReduce.reduce((total, amount) => total + amount, 0)
  }, [chargeAmount, t, goods])

  const disabledBuyButton = useMemo(() => {
    const isAllItemsCharged = chargeAmount.some((item) => item.quantity > 0)
    return !isAllItemsCharged
  }, [chargeAmount])

  const getPurchaseProcessText = useMemo(() => {
    if (productSale.chargeMode === CHARGING_ITEM.INDIVIDUAL) {
      return t('IIndividualPurchaseProcess')
    }
    if (productSale.chargeMode === CHARGING_ITEM.BATCH) {
      return t('IBatchPurchaseProcess')
    }
    return ''
  }, [productSale])

  const handleBuyProduct = () => {
    if (!isReadTerms) {
      onOpenFailureModal(t('IAgreeToTermsBeforePurchase'))
    } else {
      onSetIsAgreeToPurchase(true)
      sessionStorage.setItem(
        'productChargeAmount',
        JSON.stringify(chargeAmount),
      )
      navigate(
        `${RouteConstant.PRODUCTS_PAYMENT.path.replace(
          ':id',
          goodsId,
        )}?amount=${totalChargeAmount}&licenseId=${licenseId}&type=charge`,
      )
    }
  }

  const handleApplyChargeAmount = (value: number) => {
    setChargeAmount((prev) =>
      prev.map((item) => ({
        ...item,
        quantity: value,
      })),
    )
  }

  const showReportImage = (packageId: string) => {
    if (PACKAGE_AVAILABLE_IMAGE_ID.includes(packageId)) {
      dispatch(setReportDialogId(packageId))
      dispatch(openReportImageDialog())
    }
  }

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (ref.current && open) {
        ref.current.innerHTML = contentGuide
      }
    }, 0)

    return () => clearTimeout(timeout)
  }, [i18n.language, open])

  useEffect(() => {
    if (
      productSale.chargeMode === CHARGING_ITEM.BATCH &&
      productSale.chargeQuantity !== null
    ) {
      setManualChargeQuantity(productSale.chargeQuantity)
      handleApplyChargeAmount(productSale.chargeQuantity)
    }
  }, [productSale])

  return (
    <Dialog open={open} onClose={onClose} className={classes.container}>
      <div className={classes.closeButtonWrap}>
        <IconButton
          color='secondary'
          aria-label='favorite'
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon className={classes.closeIcon} />
        </IconButton>
      </div>
      <DialogContent className={classes.contentWrap}>
        <div className={classes.headerDialog}>
          <div className={classes.productInfo}>
            <div className={classes.headerTitle}>
              <h2 className={classes.productTitle}>{productName}</h2>
              <Button
                color='primary'
                variant='contained'
                onClick={handleBuyProduct}
                disabled={disabledBuyButton}
              >
                {t('IBuyV1')}
              </Button>
            </div>
            <p className={classes.productType}>{paymentMethod}</p>

            <div className={classes.price}>
              {targetCurrency} {formatPriceNumber(totalChargeAmount)}
            </div>
          </div>
        </div>
        <div className={classes.periodSection}>
          <div className={classes.periodText}>
            {t('IDurationV2')} :{' '}
            <span className={classes.periodHighlight}>
              {duration > 10000000
                ? t('IUnlimited')
                : `${duration} ${t('IMonths')}`}
            </span>
            <br />
            {t('IDescription')} :{' '}
            <span className={classes.description}>{description}</span>
          </div>
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <div
            style={{
              color: 'black',
              fontSize: 16,
            }}
          >
            {getPurchaseProcessText}
          </div>
          {productSale.chargeMode === CHARGING_ITEM.BATCH &&
            productSale.chargeQuantity === 0 && (
              <Fragment>
                <input
                  style={{
                    width: 80,
                  }}
                  className={classes.inputAmount}
                  type='number'
                  value={manualChargeQuantity}
                  onChange={(e) =>
                    setManualChargeQuantity(Number(e.target.value))
                  }
                />
                <Button
                  color='primary'
                  variant='contained'
                  onClick={() => handleApplyChargeAmount(manualChargeQuantity)}
                >
                  {t('IApply')}
                </Button>
              </Fragment>
            )}
        </div>
        <Table size='small' className={classes.table}>
          <TableHead className={classes.tableHeader}>
            {CHARGE_TABLE_DATA.map((header) => (
              <TableCell
                align='center'
                padding='checkbox'
                key={header.id}
                style={{
                  whiteSpace: 'pre',
                  border: '1px solid rgba(224, 224, 224, 1)',
                  padding: 8,
                }}
              >
                {t(header.label)}
              </TableCell>
            ))}
          </TableHead>
          <TableBody>
            {data.map((row) => {
              const isFirstOccurCls = !seenCls.has(row.cls)
              if (isFirstOccurCls) seenCls.add(row.cls)
              return (
                <TableRow key={row.pkgItemId}>
                  {isFirstOccurCls && (
                    <TableCell
                      align='center'
                      padding='checkbox'
                      rowSpan={rowSpanMap[row.cls]}
                      className={classes.headerMax}
                    >
                      {row.cls}
                    </TableCell>
                  )}
                  <TableCell
                    align='center'
                    padding='checkbox'
                    className={classes.headerMax}
                    style={{
                      whiteSpace: 'pre',
                    }}
                  >
                    <div
                      className={
                        PACKAGE_AVAILABLE_IMAGE_ID.includes(row.pkgItemId)
                          ? classes.clickableText
                          : ''
                      }
                      onClick={() => showReportImage(row.pkgItemId)}
                    >
                      {row.pkgName}
                    </div>
                  </TableCell>
                  <TableCell
                    align='center'
                    padding='checkbox'
                    className={classes.headerMax}
                  >
                    {targetCurrency}{' '}
                    {formatPriceNumber(getPriceByLanguage(row.unitPrices))}
                  </TableCell>
                  <TableCell
                    align='center'
                    padding='checkbox'
                    className={classes.headerMax}
                  >
                    {row.offerAmount > 10000000
                      ? t('IUnlimited')
                      : `${formatPriceNumber(
                          row.offerAmount - row.usageAmount,
                        )} ${row.cls === 'PBM' ? t('IMinute') : t('ICase')}`}
                  </TableCell>
                  <TableCell
                    align='center'
                    padding='checkbox'
                    className={classes.headerMax}
                  >
                    <input
                      className={classes.headerInput}
                      value={
                        chargeAmount.find(
                          (charge) => charge.pkgItemId === row.pkgItemId,
                        )?.quantity || DEFAULT_ZERO_QUANTITY
                      }
                      max={row.offerAmount}
                      min={0}
                      onChange={(e) =>
                        onChangeChargeAmount(
                          row.pkgItemId,
                          Number(e.target.value),
                        )
                      }
                      type='number'
                      disabled={productSale.chargeMode === CHARGING_ITEM.BATCH}
                    />
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
        <div className={classes.userGuide}>
          <span>{t('ITermsOfUse')}</span>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
            }}
          >
            (
            <Checkbox
              checked={isReadTerms}
              onChange={() => setIsReadTerms(!isReadTerms)}
            />
            <div className={classes.readTermsText}>{t('IIAgreeToTerms')}</div>)
          </div>
        </div>
        <div ref={ref} />
      </DialogContent>
    </Dialog>
  )
}

export default ChargeProductDialog
